import { usePermissions } from "@/hooks/use-permissions";
import { client, DeviceConfigDto, ScheduleJobScheduleDto } from "@/client";
import { useFinalQuery } from "@/hooks/use-finalQuery";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ClustersTableContext } from "./-clusters-table-context";
import { toast } from "sonner";
import { useTags } from "@/hooks/use-tags";
import { ManageTagsComponent } from "@/components/select-input/manage-tags-input";
import { ScheduledModelDeployment } from "@/components/custom/scheduled-deployments/scheduled-model-deployment";
import { ScheduledConfigDeployment } from "@/components/custom/scheduled-deployments/scheduled-config-deployment";

import { showActionErrorToast } from "@/state/error-toast";

export const DeployModelAction: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const table = ClustersTableContext.useTable();

  const { finalQuery } = useFinalQuery({
    context: ClustersTableContext,
    idsKey: "clusterIds",
    convert: (id: any) => parseInt(id, 10)!,
  });

  const deployModelBulk = useMutation({
    ...client.clusters.deployModelBulk.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
    onSuccess: (_data, variables) => {
      const isScheduled = !!variables.requestBody.schedule;
      toast.success(
        isScheduled ? t("modelScheduledForDeployment") : t("modelDeploying"),
      );
      // Invalidate queries after 5 seconds delay
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: client.devices.listSummaries.queryKey,
        });
      }, 5000);
      // Clear all selections after successful deletion
      table.setAllSelected(false);
      table.resetRowSelection();
      onClose();
    },
  });

  const onSubmit = async (
    modelId: number,
    schedule?: ScheduleJobScheduleDto,
    options?: { includeCollaboratorDevices?: boolean },
  ) => {
    await deployModelBulk.mutateAsync({
      requestBody: {
        modelId,
        query: finalQuery,
        schedule,
        includeCollaboratorDevices:
          options?.includeCollaboratorDevices ?? false,
      },
    });
  };

  return (
    <ScheduledModelDeployment
      onClose={onClose}
      apiCallback={onSubmit}
      showCollaboratorOption
    />
  );
};

export const TagsActions: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { permissions } = usePermissions();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { finalQuery, count } = useFinalQuery({
    context: ClustersTableContext,
    idsKey: "clusterIds",
    convert: (id: any) => parseInt(id, 10)!,
  });

  const updateTagsBulk = useMutation({
    ...client.clusters.updateTagsBulk.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
    onSuccess: () => {
      toast.success(t("updated"));
      onClose();
    },
  });

  // Query invalidation callbacks
  const queryInvalidationCallbacks = [
    () =>
      queryClient.invalidateQueries({
        queryKey: client.clusters.listSummaries.queryKey,
      }),
    (hasNewTags?: boolean) => {
      if (hasNewTags) {
        queryClient.invalidateQueries({
          queryKey: client.clusters.listTags.queryKey,
        });
      }
    },
  ];

  const {
    tagGroups,
    form,
    formSchema: _formSchema,
    onSubmit,
  } = useTags({
    tagsListQuery: client.clusters.listTags
      .assign({
        page: 1,
        limit: 9999,
      })
      .asQueryOptions({}),
    context: ClustersTableContext,
    finalQuery,
    updateFunction: updateTagsBulk,
    invalidationCallbacks: queryInvalidationCallbacks,
  });

  return (
    <ManageTagsComponent
      form={form}
      onSubmit={onSubmit!}
      tagGroups={tagGroups}
      canRemoveLocked={permissions.clusters.tags.unassignLocked}
      isSubmitting={updateTagsBulk.isPending}
      descriptionKey={`${t("addingEditingTagsDescription")} ${t("for")} ${count} ${count <= 1 ? t("cluster") : t("clusters")}`}
    />
  );
};

export const UploadConfigAction: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const table = ClustersTableContext.useTable();

  const { finalQuery, count } = useFinalQuery({
    context: ClustersTableContext,
    idsKey: "clusterIds",
    convert: (id: any) => parseInt(id, 10)!,
  });

  const deployConfigBulk = useMutation({
    mutationFn: async (data: {
      deviceConfig: Partial<DeviceConfigDto>;
      name?: string;
      description?: string;
      schedule?: ScheduleJobScheduleDto;
    }) => {
      return await client.clusters.deployConfigBulk({
        requestBody: {
          query: finalQuery,
          deviceConfig: data.deviceConfig,
          isPartial: true,
          name: data.name,
          description: data.description,
          schedule: data.schedule,
        },
      });
    },
    onError: (err: any) =>
      showActionErrorToast(err, "Failed to deploy configuration"),
    onSuccess: () => {
      toast.success(t("configurationDeployed"));
      queryClient.invalidateQueries({
        queryKey: client.clusters.listSummaries.queryKey,
      });
      // Clear all selections after successful deployment
      table.setAllSelected(false);
      table.resetRowSelection();
      onClose();
    },
  });

  const onSubmit = async (data: {
    deviceConfig: Partial<DeviceConfigDto>;
    name?: string;
    description?: string;
    schedule?: ScheduleJobScheduleDto;
  }) => {
    await deployConfigBulk.mutateAsync(data);
  };

  return (
    <div className="w-full mx-auto">
      <ScheduledConfigDeployment
        apiCallback={onSubmit}
        onClose={onClose}
        count={count}
      />
    </div>
  );
};
